import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrashAlt } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import styles from './ListaUsuarios.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import { UserContext } from '../../UserContext';
import Pagination from '../pagination/Pagination';
import { useHttpClient } from '../hooks/useHttpClient';
import { somenteNumeros } from '../../util/validacoes';
import { useNotificacoes } from '../hooks/useNotificacoes';

function ListaUsuarios() {
    const { user, setUser } = useContext(UserContext);
    const { get, del } = useHttpClient();
    const { exibirErro } = useNotificacoes();
    const [usuarios, setUsuarios] = useState([]);
    const [tiposUsuario, setTiposUsuario] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [filtros, setFiltros] = useState({ idTipoUsuario: '', documento: '', nome: '', idUsina: '' });
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const jaCarregou = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!user && localStorage.getItem('userData')) {
            setUser(JSON.parse(localStorage.getItem('userData')));
        }
    }, [user, setUser]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const resp = await get(`/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`);
                setUsinas(Array.isArray(resp) ? resp : resp.content ?? []);
            } catch (err) {
                console.error('Erro ao buscar usinas.' + err.message);
                exibirErro('Erro ao buscar usinas.');
            }
        })();
    }, [user, get, exibirErro]);

    useEffect(() => {
        const fetchTiposUsuario = async () => {
            try {
                const data = await get('/hello-sol/tipos-usuario');
                setTiposUsuario(data || []);
            } catch (err) {
                console.error('Erro ao buscar tipos de usuário', err);
                exibirErro('Erro ao buscar tipos de usuário.');
            }
        };
        if (tiposUsuario.length === 0) fetchTiposUsuario();
    }, [tiposUsuario.length, get, exibirErro]);

    const fetchUsuarios = useCallback(async (usarFiltros = false, page = 0) => {
        if (!user?.empresa?.id) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                cpfCnpj: usarFiltros ? filtros.documento : '',
                nome: usarFiltros ? filtros.nome : '',
                idTipoUsuario: usarFiltros ? filtros.idTipoUsuario : '',
                idUsina: usarFiltros ? filtros.idUsina : '',
                idEmpresa: String(user.empresa.id),
                page: String(page),
                pageSize: String(pageSize)
            }).toString();

            const data = await get(`/hello-sol/usuarios?${params}`);
            const lista = Array.isArray(data.content) ? data.content : [];
            setUsuarios(lista.filter(Boolean));
            setTotalPages(data.totalPages || 0);
        } catch (err) {
            console.error('Erro ao buscar usuários', err);
            exibirErro('Erro ao buscar usuários.');
        } finally {
            setLoading(false);
        }
    }, [filtros, user, pageSize, get, exibirErro]);

    useEffect(() => {
        if (!jaCarregou.current && user?.empresa?.id) {
            jaCarregou.current = true;
            fetchUsuarios(false, 0);
        }
    }, [user?.empresa?.id, fetchUsuarios]);

    const handleDelete = async (id) => {
        try {
            await del(`/hello-sol/usuarios/${id}`);
            fetchUsuarios(true, currentPage);
        } catch (err) {
            console.error('Erro ao excluir', err);
            exibirErro('Erro ao excluir.');
        }
    };

    const confirmDelete = (id) => {
        confirmAlert({
            title: 'Confirmação',
            message: 'Deseja realmente excluir este usuário?',
            buttons: [
                { label: 'Sim', onClick: () => handleDelete(id) },
                { label: 'Não' }
            ]
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            fetchUsuarios(true, newPage);
        }
    };

    const formatDocumento = (value) => {
        if (!value) return 'Pendente';
        return value.length > 11
            ? value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5')
            : value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, '$1.$2.$3-$4');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({
            ...prev,
            [name]: name === 'documento' ? somenteNumeros(value) : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        fetchUsuarios(true, 0);
    };

    return (
        <div className={styles_global.container}>
            <h2>Consultar Usuários</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                <div className={`${styles_content.formField} ${styles.usinaField}`}>
                    <label>Usina</label>
                    <select
                        name="idUsina"
                        value={filtros.idUsina}
                        onChange={handleChange}
                    >
                        <option value="">Selecione a Usina</option>
                        {usinas.map((usina) => (
                            <option key={usina.id} value={usina.id}>
                                {usina.nome}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={`${styles_content.formField} ${styles.usuarioField}`}>
                    <label htmlFor="usuario-tipo">Tipo de Usuário</label>
                    <select
                        id="usuario-tipo"
                        name="idTipoUsuario"
                        value={filtros.idTipoUsuario}
                        onChange={handleChange}
                    >
                        <option value="">Selecione o Usuário</option>
                        {tiposUsuario.map((tipo) => (
                            <option key={tipo.id} value={tipo.id}>
                                {tipo.descricao}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={`${styles_content.formField} ${styles.cpfField}`}>
                    <label htmlFor="usuario-documento">CPF/CNPJ</label>
                    <input
                        id="usuario-documento"
                        name="documento"
                        value={filtros.documento}
                        onChange={handleChange}
                        placeholder="Digite o CPF ou CNPJ"
                        maxLength={18}
                    />
                </div>
                <div className={`${styles_content.formField} ${styles.nomeField}`}>
                    <label htmlFor="usuario-nome">Nome</label>
                    <input
                        id="usuario-nome"
                        name="nome"
                        value={filtros.nome}
                        onChange={handleChange}
                        placeholder="Digite o Nome"
                        maxLength={100}
                    />
                </div>
                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                    <button type="button" onClick={() => navigate('/home/cadastrar-usuario')}>
                        Novo Usuário
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && usuarios.length > 0 && (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>USUÁRIO</th>
                            <th>STATUS</th>
                            <th>CPF/CNPJ</th>
                            <th>NOME</th>
                            <th>AÇÕES</th>
                        </tr>
                        </thead>
                        <tbody>
                        {usuarios.map((usuario) => (
                            <tr key={usuario.id}>
                                <td>{usuario.descricaoTipoUsuario || '—'}</td>
                                <td>
                                    {usuario.cadastroPendente ? (
                                        <span className={styles.badgePendente}>Pendente</span>
                                    ) : (
                                        <span className={styles.badgeOk}>Regular</span>
                                    )}
                                </td>
                                <td>{formatDocumento(usuario.cpfCnpj)}</td>
                                <td>
                                    {usuario.nome || '—'}
                                    {usuario.cadastroPendente && usuario.origemCadastro && (
                                        <div className={styles.subInfo}>{usuario.origemCadastro}</div>
                                    )}
                                </td>
                                <td className={styles.actions}>
                                    <Link to={`/home/detalhar-usuario/${usuario.id}`} className={styles.iconButton}>
                                        <FaEye className={styles.icon} />
                                    </Link>
                                    <Link to={`/home/cadastrar-usuario/${usuario.id}`} className={styles.iconButton}>
                                        <FaEdit className={styles.icon} />
                                    </Link>
                                    <Link to="#" onClick={() => confirmDelete(usuario.id)} className={styles.iconButton}>
                                        <FaTrashAlt className={`${styles.icon} ${styles.delete}`} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            {!loading && usuarios.length === 0 && (
                <p className={styles_global.noData}>Nenhum usuário encontrado.</p>
            )}
        </div>
    );
}

export default ListaUsuarios;
